import { useLazyQuery } from '@apollo/client';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SELLABLE_BY_BARCODE_QUERY } from './doctor-pos.gql';
import type { SellableHit } from './SellableSearchPanel';

import { Input } from '@/shared/ui/input';

interface BarcodeByCodeResponse {
  sellableByBarcode: SellableHit | null;
}

interface BarcodeScanBoxProps {
  on_add: (hit: SellableHit) => void;
  disabled?: boolean;
}

export function BarcodeScanBox({ on_add, disabled }: BarcodeScanBoxProps): JSX.Element {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [not_found, setNotFound] = useState(false);
  const [unavailable, setUnavailable] = useState(false);

  const [runLookup, { loading }] = useLazyQuery<BarcodeByCodeResponse>(SELLABLE_BY_BARCODE_QUERY, {
    fetchPolicy: 'network-only',
  });

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    const trimmed = code.trim();
    if (trimmed === '') return;
    setNotFound(false);
    setUnavailable(false);
    const { data } = await runLookup({ variables: { code: trimmed } });
    const hit = data?.sellableByBarcode ?? null;
    if (!hit) {
      setNotFound(true);
    } else if (hit.available === false) {
      setUnavailable(true);
    } else {
      on_add(hit);
      setCode('');
    }
  };

  return (
    <form
      onSubmit={(event) => {
        void onSubmit(event);
      }}
    >
      <Input
        type="text"
        inputMode="text"
        autoComplete="off"
        placeholder={t('inventory.barcode_placeholder')}
        aria-label={t('inventory.scan_barcode')}
        value={code}
        onChange={(event) => {
          setCode(event.target.value);
          if (not_found) setNotFound(false);
          if (unavailable) setUnavailable(false);
        }}
        disabled={disabled === true || loading}
      />
      {not_found ? (
        <p className="text-destructive mt-1 text-xs">{t('inventory.barcode_not_found')}</p>
      ) : null}
      {unavailable ? (
        <p className="text-destructive mt-1 text-xs">{t('inventory.barcode_unavailable')}</p>
      ) : null}
    </form>
  );
}
